/**
 * ArAgingTab — Reporte de antigüedad de saldos CxC con tabla y resumen.
 */
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { useState } from 'react'
import { Download, TrendingUp, CheckCircle2 } from 'lucide-react'
import { useArAging, useDso } from '../hooks/useFinance'
import { useTenantLocale, formatNumber } from '@/lib/locale'
import DownloadButton from '@/components/DownloadButton'
import { useLocalizationConfig } from '@/modules/config/hooks/useConfig'
import { AGING_BUCKETS, agingBucket } from '@/components/ds/agingBuckets'

export default function ArAgingTab() {
  const { data, isLoading } = useArAging()
  const { data: dsoData } = useDso(6)
  const { data: locConfig } = useLocalizationConfig()
  const locale = useTenantLocale()
  const currency = locConfig?.currency || 'CLP'
  const isCLP = currency === 'CLP'
  const fmt = (value: string | number) => {
    const n = typeof value === 'string' ? parseFloat(value) : value
    const decimals = isCLP ? 0 : (n % 1 !== 0 ? 2 : 0)
    return `$${formatNumber(n, locale, decimals)}`
  }

  const summary = data?.summary
  const customers = data?.by_customer || []

  return (
    <div data-tour="aging-page">
      {/* Currency indicator */}
      <p className="text-xs text-content-muted mb-3">
        Montos expresados en <span className="font-mono font-semibold text-content">{currency}</span>
        {locConfig?.currency_display && <span> ({locConfig.currency_display})</span>}
      </p>

      {/* Summary cards */}
      <div data-tour="aging-summary" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {summary && AGING_BUCKETS.map(({ key, label }) => (
          <div key={key} className="bg-glass backdrop-blur-glass border border-divider rounded-card p-4 shadow-card">
            <p className="text-xs text-content-muted font-medium">{label}</p>
            <p className="text-lg font-bold text-content mt-1">
              {fmt(summary[key as keyof typeof summary] || '0')}
            </p>
          </div>
        ))}
        <div className="bg-primary-50 border border-primary-100 rounded-xl p-4">
          <p className="text-xs text-primary font-medium">Total CxC</p>
          <p className="text-lg font-bold text-primary mt-1">
            {fmt(summary?.total || '0')}
          </p>
        </div>
      </div>

      {/* DSO indicator */}
      {dsoData && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-glass backdrop-blur-glass border border-divider rounded-card shadow-card">
          <TrendingUp size={18} className="text-primary" />
          <span className="text-sm text-content-muted">
            {dsoData.current_dso ? (
              <>
                DSO <strong className="text-content">{dsoData.current_dso} días</strong>
                {dsoData.current_period && (
                  <span className="text-content-subtle"> · {dsoData.current_period}</span>
                )}
              </>
            ) : (
              /* Sin ventas en el período no hay DSO que calcular. Mostrar «0
                 días» diría que se cobra al contado, que es lo contrario. */
              <>DSO <strong className="text-content">sin dato</strong>{' '}
                <span className="text-content-subtle">— sin ventas suficientes en los meses cerrados para un valor representativo</span></>
            )}
          </span>
          <DownloadButton path="/finance/receivables/aging/export/" filename="aging_cxc.csv"
            label="Exportar CSV" iconSize={12} dataTour="aging-export"
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-divider rounded-lg hover:bg-soft" />
        </div>
      )}

      {/* Aging table by customer */}
      <div data-tour="aging-table">
        <DataTable
          columns={[
            {
              key: 'cliente', header: 'Cliente', sortable: true,
              sortValue: (c: any) => c.customer_name,
              render: (c: any) => <span className="font-medium text-content">{c.customer_name}</span>,
            },
            {
              key: 'rut', header: 'RUT', sortable: true,
              sortValue: (c: any) => c.customer_tax_id,
              render: (c: any) => <span className="font-mono">{c.customer_tax_id}</span>,
            },
              {
                key: 'current', header: agingBucket('current').short, numeric: true, sortable: true,
                sortValue: (c: any) => Number(c['current']),
                render: (c: any) => fmt(c['current']),
              },
              {
                key: '1_30', header: agingBucket('1_30').short, numeric: true, sortable: true,
                sortValue: (c: any) => Number(c['1_30']),
                render: (c: any) => fmt(c['1_30']),
              },
              {
                key: '31_60', header: agingBucket('31_60').short, numeric: true, sortable: true,
                sortValue: (c: any) => Number(c['31_60']),
                render: (c: any) => fmt(c['31_60']),
              },
              {
                key: '61_90', header: agingBucket('61_90').short, numeric: true, sortable: true,
                sortValue: (c: any) => Number(c['61_90']),
                render: (c: any) => fmt(c['61_90']),
              },
            {
              key: 'over_90', header: agingBucket('over_90').short, numeric: true, sortable: true,
              sortValue: (c: any) => Number(c.over_90),
              render: (c: any) => (
                <span className="font-medium text-danger-content-soft">{fmt(c.over_90)}</span>
              ),
            },
            {
              key: 'total', header: 'Total', numeric: true, sortable: true,
              sortValue: (c: any) => Number(c.total),
              render: (c: any) => <span className="font-bold">{fmt(c.total)}</span>,
            },
          ]}
          rows={customers}
          rowKey={(c: any) => c.customer_id}
          loading={isLoading}
          empty={<EmptyState icon={CheckCircle2} size="sm" title="Sin saldos pendientes." />}
          minWidth={900}
          caption="Antigüedad de saldos por cobrar"
        />
      </div>
    </div>
  )
}
